import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Stack, Typography, Paper, Button, CircularProgress, Avatar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem, Breadcrumbs,
    Link as MuiLink, IconButton, Drawer, Divider
} from "@mui/material";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import DeleteIcon from '@mui/icons-material/Delete';
// Icons
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import TollIcon from '@mui/icons-material/Toll'; // Icône pour les jetons
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditIcon from '@mui/icons-material/Edit';
import AppSearch from "../../components/base/AppSearch";
import AppPagination from "../../components/base/AppPagination";
import ConfirmDialog from "../../components/base/ConfirmDialog";
const ClientsPackVideo = () => {
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [clientPacks, setClientPacks] = useState([]);
    const [availablePacks, setAvailablePacks] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [selectedPack, setSelectedPack] = useState(null);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [openAddModal, setOpenAddModal] = useState(false);

    const [formData, setFormData] = useState({
        client_email: '',
        id: '',
        quantity: 1
    });

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiUrl}/admin/client-video-packs/all/back`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setClientPacks(response.data.result || []);
        } catch (err) {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token]);

    const fetchPacksCatalog = useCallback(async () => {
        try {
            const response = await axios.get(`${apiUrl}/admin/video-packs/back/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailablePacks(response.data.result || []);
        } catch (err) {
            console.error("Erreur catalogue", err);
        }
    }, [apiUrl, token]);

    useEffect(() => {
        fetchData();
        fetchPacksCatalog();
    }, [fetchData, fetchPacksCatalog]);

    const handleOpenDetails = (pack) => {
        setSelectedPack(pack);
        setOpenDrawer(true);
    };

    const handleAssignPack = async () => {
        try {
            await axios.post(`${apiUrl}/admin/save-client-pack-video/back/admin`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Pack attribué !");
            setOpenAddModal(false);
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de l'attribution");
        }
    };

    const filteredData = clientPacks.filter(item => {
        const search = searchTerm.toLowerCase();
        return (
            item.client?.nom_client?.toLowerCase().includes(search) ||
            item.video_pack?.name?.toLowerCase().includes(search) ||
            item.client?.email_client?.toLowerCase().includes(search)
        );
    });

    const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // 2. Exécute la suppression réelle
    // À ajouter dans vos useState au début du composant
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [packToDelete, setPackToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    // Delete clients pack jetons 
    // 1. Ouvre la modale et mémorise l'ID
    const confirmDelete = (id) => {
        setPackToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        try {
            setDeleteLoading(true);
            await axios.delete(`${apiUrl}/admin/delete-client-pack-video/back/${packToDelete}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success("Pack jetons client supprimé avec succès");
            fetchData(); // Rafraîchir le tableau
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        } finally {
            setDeleteLoading(false);
            setDeleteDialogOpen(false);
            setPackToDelete(null);
        }
    };

    // update pack client jetons 
    const [isEditMode, setIsEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const handleOpenEdit = (pack) => {
        setIsEditMode(true);
        setEditId(pack.id);
        setFormData({
            email_client: pack.client?.email_client || '', // Généralement readonly en edit
            id: pack.video_pack?.id || '', // C'est l'ID du pack (video_pack_id)
            quantity: pack.quantity || 1
        });
        setOpenAddModal(true);
    };

    const handleUpdatePack = async () => {
        try {
            setLoading(true);
            const body = {
                video_pack_id: formData.id,
                quantity: formData.quantity
            };

            await axios.put(`${apiUrl}/admin/update-client-pack-video/back/${editId}`, body, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success("Pack jetons client mis à jour avec succès");
            setOpenAddModal(false);
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        } finally {
            setLoading(false);
        }
    };
    return (
        <Box sx={{ p: 3, bgcolor: "#F4F7FE", minHeight: "100vh" }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                <MuiLink component={Link} underline="hover" color="inherit" to="/clients">Clients</MuiLink>
                <Typography color="text.primary" sx={{ fontWeight: 600 }}>Packs Vidéos Clients</Typography>
            </Breadcrumbs>

            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#1B2559" }}>Historique des Packs</Typography>
                <Stack direction="row" spacing={2}>
                    <AppSearch value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher..." />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setIsEditMode(false);
                            setFormData({ email_client: '', id: '', quantity: 1 });
                            setOpenAddModal(true);
                        }}
                        sx={{
                                bgcolor: "#4318FF",
                                borderRadius: "20px", // Plus arrondi pour l'effet "pilule"
                                px: 8,               // Réduit le padding (12 était trop large)
                                py: 1.5,             // Ajuste le padding vertical au lieu d'une hauteur fixe
                                textTransform: 'none',
                                fontSize: '14px',    // Taille de police standard dashboard
                                fontWeight: 600,     // Pour plus de lisibilité
                                whiteSpace: 'nowrap', // Force le texte sur une seule ligne
                                boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)', // Optionnel: petite ombre portée
                                "&:hover": {
                                    bgcolor: "#3311CC",
                                    boxShadow: 'none'
                                }
                            }}
                    >
                        Ajouter un pack client
                    </Button>
                </Stack>
            </Stack>

            <Paper sx={{ borderRadius: "20px", boxShadow: "0px 20px 50px rgba(112, 144, 176, 0.08)", overflow: "hidden" }}>
                {loading ? (
                    <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: "#F4F7FE" }}>
                                <TableRow>
                                    <TableCell sx={{ color: "#A3AED0", fontWeight: "700" }}>CLIENT</TableCell>
                                    <TableCell sx={{ color: "#A3AED0", fontWeight: "700" }}>PACK & JETONS</TableCell>
                                    <TableCell sx={{ color: "#A3AED0", fontWeight: "700" }}>PRIX TOTAL</TableCell>
                                    <TableCell sx={{ color: "#A3AED0", fontWeight: "700" }}>DATE D'ACHAT</TableCell>
                                    <TableCell align="right" sx={{ color: "#A3AED0", fontWeight: "700" }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {currentItems.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#1B2559" }}>
                                                {row.client?.nom_client} {row.client?.prenom_client}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">{row.client?.email_client}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontWeight: 600 }}>{row.video_pack?.name}</Typography>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <TollIcon sx={{ fontSize: 14, color: "#FFB547" }} />
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#FFB547" }}>
                                                    {row.video_pack?.token_amount * row.quantity} jetons
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#05CD99" }}>
                                            {(row.video_pack?.price * row.quantity).toFixed(2)} €
                                        </TableCell>
                                        <TableCell>{new Date(row.purchase_date).toLocaleDateString()}</TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <IconButton onClick={() => handleOpenDetails(row)} color="primary">
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                                {/* NOUVEAU BOUTON EDIT */}
                                                <IconButton
                                                    onClick={() => handleOpenEdit(row)}
                                                    sx={{
                                                        color: "#4318FF",
                                                        '&:hover': { bgcolor: "rgba(67, 24, 255, 0.1)" }
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                {/* Nouveau bouton Delete */}
                                                <IconButton onClick={() => confirmDelete(row.id)} sx={{ color: "#EE5D50" }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {/* Pagination */}
                {!loading && filteredData.length > 0 && (
                    <AppPagination
                        count={Math.ceil(filteredData.length / itemsPerPage)}
                        page={currentPage}
                        onChange={(e, v) => setCurrentPage(v)}
                        totalItems={filteredData.length} // Nombre total d'éléments après filtrage
                        itemsPerPage={itemsPerPage}      // Fixé à 8 dans votre code
                    />
                )}            </Paper>

            {/* --- DRAWER DÉTAILS --- */}
            <Drawer
                anchor="right"
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
                sx={{ zIndex: 3000 }}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 550 },
                        p: 0, // On gère le padding à l'intérieur pour le design
                        borderLeft: "1px solid #E0E5F2"
                    }
                }}>
                {selectedPack && (
                    <Box>
                        {/* Header du volet */}
                        <Box sx={{ p: 3, bgcolor: "#4318FF", color: "white" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" fontWeight={800}>Fiche Détail Vente</Typography>
                                <IconButton onClick={() => setOpenDrawer(false)} sx={{ color: "white" }}><CloseIcon /></IconButton>
                            </Stack>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>Référence : #{selectedPack.id}</Typography>
                        </Box>

                        <Box sx={{ p: 3 }}>
                            {/* --- SECTION 1 : IDENTITÉ DU CLIENT --- */}
                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1.5, fontWeight: 700 }}>IDENTITÉ DU CLIENT</Typography>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                <Avatar sx={{ width: 56, height: 56, bgcolor: "#F4F7FE", color: "#4318FF" }}>
                                    <PersonIcon fontSize="large" />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        {selectedPack.client?.nom_client} {selectedPack.client?.prenom_client}
                                    </Typography>
                                    <Typography variant="body2" color="#4318FF" sx={{ fontWeight: 600 }}>
                                        {selectedPack.client?.email_client}
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* --- SECTION 2 : COORDONNÉES DE CONTACT --- */}
                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 700 }}>COORDONNÉES & LOCALISATION</Typography>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px", mb: 3, bgcolor: "#fcfcfc" }}>
                                <Stack spacing={1.5}>
                                    <Stack direction="row" spacing={2}>
                                        <Typography variant="body2" sx={{ color: "#A3AED0", width: 100 }}>Téléphone :</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedPack.client?.phone_client || "Non renseigné"}</Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <Typography variant="body2" sx={{ color: "#A3AED0", width: 100 }}>Adresse :</Typography>
                                        <Typography variant="body2">{selectedPack.client?.addresse_client || "N/A"}</Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <Typography variant="body2" sx={{ color: "#A3AED0", width: 100 }}>Ville :</Typography>
                                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                            {selectedPack.client?.ville_client} ({selectedPack.client?.cp_client})
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        <Typography variant="body2" sx={{ color: "#A3AED0", width: 100 }}>Province :</Typography>
                                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedPack.client?.province_client}</Typography>
                                    </Stack>
                                </Stack>
                            </Paper>

                            <Divider sx={{ mb: 3 }} />

                            {/* --- SECTION 3 : RÉCAPITULATIF DU PACK --- */}
                            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 700 }}>PRODUIT ACHETÉ</Typography>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px", bgcolor: "#F4F7FE", border: "none", mb: 2 }}>
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2">Pack sélectionné :</Typography>
                                        <Typography variant="body2" fontWeight={700}>{selectedPack.video_pack?.name}</Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2">Crédit Jetons :</Typography>
                                        <Typography variant="body2" fontWeight={800} sx={{ color: "#FFB547" }}>
                                            {selectedPack.video_pack?.token_amount} 🪙
                                        </Typography>
                                    </Stack>
                                    <Divider />
                                    <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={700}>Total Payé :</Typography>
                                        <Typography variant="subtitle1" fontWeight={800} color="#05CD99">
                                            {(selectedPack.video_pack?.price * selectedPack.quantity).toFixed(2)} €
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Paper>

                            {/* Description du contenu */}
                            <Box sx={{ p: 2, bgcolor: "#FFF8E1", borderRadius: "12px", borderLeft: "4px solid #FFB547", mb: 4 }}>
                                <Typography variant="caption" sx={{ color: "#FFB547", fontWeight: 700, display: 'block', mb: 0.5 }}>CONTENU DU PACK :</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: "#555" }}>
                                    {selectedPack.video_pack?.description}
                                </Typography>
                            </Box>

                            {/* Date et bouton */}
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "text.secondary", mb: 3 }}>
                                <CalendarMonthIcon fontSize="small" />
                                <Typography variant="caption">Transaction du {new Date(selectedPack.purchase_date).toLocaleString('fr-FR')}</Typography>
                            </Stack>

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => setOpenDrawer(false)}
                                sx={{ bgcolor: "#1B2559", borderRadius: "12px", py: 1.5, textTransform: 'none', fontWeight: 700 }}
                            >
                                Fermer le profil
                            </Button>
                        </Box>
                    </Box>
                )}
            </Drawer>

            {/* Modal Ajout - inchangé */}
            <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 800 }}>Vente manuelle</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {/* Correction : email_client */}
                        <TextField
                            fullWidth
                            label="Email Client"
                            value={formData.email_client}
                            onChange={(e) => setFormData({ ...formData, email_client: e.target.value })}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Pack</InputLabel>
                            {/* Correction : id */}
                            <Select
                                value={formData.id}
                                label="Pack"
                                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                            >
                                {availablePacks.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.name} ({p.token_amount} jetons)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            type="number"
                            label="Quantité"
                            value={formData.quantity}
                            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenAddModal(false)}>Annuler</Button>
                    <Button onClick={handleAssignPack} variant="contained" sx={{ bgcolor: "#4318FF" }}>
                        Valider
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDelete}
                title="Confirmer la suppression"
                message="Voulez-vous vraiment supprimer cet achat ? Cette action déduira également les jetons du solde du client."
                loading={deleteLoading}
            />
            <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {isEditMode ? "Modifier la vente" : "Vente manuelle"}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Email Client"
                            value={formData.email_client}
                            disabled={isEditMode} // On ne change pas le client lors d'une édition
                            onChange={(e) => setFormData({ ...formData, email_client: e.target.value })}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Pack</InputLabel>
                            <Select
                                value={formData.id}
                                label="Pack"
                                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                            >
                                {availablePacks.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.name} ({p.token_amount} jetons)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            type="number"
                            label="Quantité"
                            value={formData.quantity}
                            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenAddModal(false)}>Annuler</Button>
                    <Button
                        onClick={isEditMode ? handleUpdatePack : handleAssignPack}
                        variant="contained"
                        sx={{ bgcolor: "#4318FF" }}
                    >
                        {isEditMode ? "Mettre à jour" : "Valider"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>

    );
};

export default ClientsPackVideo;